// Pure utility functions — no DOM, no side effects.

/**
 * Turkish VKN (10-digit Tax ID) checksum validation.
 * Reference: Turkish Revenue Administration algorithm.
 */
export function isValidTurkishVkn(input: string): boolean {
  const v = (input || "").replace(/\D/g, "");
  if (v.length !== 10) return false;
  const digits = v.split("").map((d) => parseInt(d, 10));
  const last = digits[9];
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const tmp = (digits[i] + (9 - i)) % 10;
    sum += tmp === 0 ? 0 : (tmp * Math.pow(2, 9 - i)) % 9 || 9;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === last;
}

export function formatVkn(raw: string): string {
  return (raw || "").replace(/\D/g, "").slice(0, 10);
}

/**
 * Loose E.164 validator: + followed by 8-15 digits.
 */
export function isValidE164(phone: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test((phone || "").replace(/\s|-/g, ""));
}

export function formatE164(raw: string): string {
  const cleaned = (raw || "").replace(/[^\d+]/g, "");
  if (!cleaned) return "";
  if (cleaned.startsWith("+")) return "+" + cleaned.slice(1).replace(/\D/g, "").slice(0, 15);
  return "+" + cleaned.replace(/\D/g, "").slice(0, 15);
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
}

export function isValidPostalCode(code: string, countryCode = "TR"): boolean {
  const c = (code || "").trim();
  if (countryCode === "TR") return /^\d{5}$/.test(c);
  return c.length >= 3 && c.length <= 10;
}

export type StrengthLevel = 0 | 1 | 2 | 3 | 4;

export interface StrengthResult {
  score: StrengthLevel;
  hasLetter: boolean;
  hasDigit: boolean;
  hasSymbol: boolean;
  hasMinLength: boolean;
  isCommon: boolean;
}

const COMMON_PATTERNS = [
  /^password/i,
  /^123456/,
  /^qwerty/i,
  /^letmein/i,
  /^admin/i,
  /^welcome/i,
];

export function scorePassword(password: string): StrengthResult {
  const pw = password || "";
  const hasLetter = /[A-Za-zçğıöşüÇĞİÖŞÜ]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSymbol = /[^A-Za-z0-9çğıöşüÇĞİÖŞÜ]/.test(pw);
  const hasMinLength = pw.length >= 8;
  const isCommon = COMMON_PATTERNS.some((re) => re.test(pw));

  let raw = 0;
  if (hasMinLength) raw++;
  if (pw.length >= 12) raw++;
  if (hasLetter && hasDigit) raw++;
  if (hasSymbol) raw++;
  if (isCommon) raw = Math.max(0, raw - 2);

  const score = Math.max(0, Math.min(4, raw)) as StrengthLevel;
  return { score, hasLetter, hasDigit, hasSymbol, hasMinLength, isCommon };
}
